// "Cronograma de desembolso" no Editar Obra (change 00, D7): só o ADM Ampli (papel `admin`)
// vê, e só numa obra cuja organização usa o desembolso Ampli. Mostra a origem em uso, a
// cobertura e as contagens, diz de qual previsto o reset puxa — sem escolha de origem: o
// servidor resolve (Prevision com a integração configurada, senão o previsto do ERP) — e
// oferece "Resetar cronograma" com confirmação.
//
// Independente do formulário da obra: botões próprios (`type="button"`), fora de
// `handleSend`. Salvar a obra não reseta o cronograma, e resetar não salva a obra.
import { useCallback, useEffect, useState } from "react";

import {
  fetchDisbursementRules,
  normalizeDisbursementList,
  saveDisbursementOrigin,
} from "@components/dashboard/disbursement/disbursementApi";
import { projectedCoverage } from "@components/dashboard/disbursement/coverage";
import { reais } from "@components/dashboard/disbursement/monthEdit";
import {
  DISCARD_REASON_LABELS,
  ORIGIN_KIND_LABELS,
  originStateText,
} from "@components/dashboard/disbursement/originChain";
import { fractionLabel } from "@components/dashboard/disbursement/ProjectedCoverage";
import { isPresentationProject } from "@utils/presentationMode";

/** De onde o reset puxa: "Previsto (Prevision)", "Físico-financeiro do Sienge" ou "Previsto mensal do MEGA". */
export const resetSourceLabel = ({ origin, erpSource }) => {
  if (origin === "prevision") return "Previsto (Prevision)";
  return String(erpSource ?? "").toLowerCase() === "mega" ? "Previsto mensal do MEGA" : "Físico-financeiro do Sienge";
};

const resolvedByText = (resolvedBy) => (resolvedBy === "prevision_integration"
  ? "integração Prevision configurada"
  : "sem integração Prevision");

const count = (value, singular, plural) => `${(value ?? 0).toLocaleString("pt-BR")} ${value === 1 ? singular : plural}`;

const BuildingDisbursementSchedule = ({ projectId, building = null }) => {
  // `hidden`: a organização não usa o desembolso Ampli (409) — a seção não aparece.
  const [state, setState] = useState({ status: "loading" });
  const [confirming, setConfirming] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    const [rules, preview] = await Promise.all([
      fetchDisbursementRules(projectId),
      saveDisbursementOrigin(projectId, { dryRun: true }),
    ]);
    if (preview?.code === 409 && preview?.errorCode === "ampli_disbursement_disabled") {
      setState({ status: "hidden" });
      return;
    }
    if (preview?.code !== 200) {
      setState({
        status: "error",
        message: preview?.code === 403 && preview?.errorCode === "project_out_of_scope"
          ? "A obra precisa estar atribuída ao seu usuário para ver o cronograma de desembolso."
          : "Não foi possível carregar o cronograma de desembolso desta obra.",
      });
      return;
    }
    const list = rules ? normalizeDisbursementList(rules) : null;
    setState({ status: "ready", preview, origin: list?.origin ?? null, coverage: list?.coverage ?? null });
  }, [projectId]);

  useEffect(() => {
    if (projectId) load();
  }, [projectId, load]);

  if (!projectId || state.status === "hidden") return null;

  const confirmReset = async () => {
    setResetting(true);
    const answer = await saveDisbursementOrigin(projectId, { reset: true });
    setResetting(false);
    setConfirming(false);
    if (answer?.code === 200 || answer?.code === 202) {
      setResult({ ok: true, reset: answer.reset ?? null, origin: answer.origin ?? null });
      await load();
      return;
    }
    setResult({
      ok: false,
      message: answer?.errorCode === "origin_ineligible"
        ? `O previsto da obra não está disponível: ${DISCARD_REASON_LABELS[answer?.details?.reason] ?? "fonte indisponível nesta obra"}.`
        : answer?.message ?? "Não foi possível resetar o cronograma.",
    });
  };

  const { preview, origin, coverage } = state;
  const projected = projectedCoverage(coverage);
  const presentation = state.status === "ready" && isPresentationProject({ ...building, wbs_code_id: preview?.wbs });
  const source = preview ? resetSourceLabel(preview) : null;

  return (
    <section className="building-disbursement" aria-labelledby="building-disbursement-title" data-testid="building-disbursement">
      <h3 id="building-disbursement-title">Cronograma de desembolso</h3>
      {state.status === "loading" ? <p role="status">Carregando o cronograma de desembolso…</p> : null}
      {state.status === "error" ? <p className="alert alert-error" role="alert">{state.message}</p> : null}
      {state.status === "ready" ? (
        <>
          <dl className="building-disbursement__facts">
            <div>
              <dt>Origem</dt>
              <dd>{originStateText(origin)?.replace(/^Origem: /, "").replace(/\.$/, "") ?? "—"}</dd>
            </div>
            <div>
              <dt>Cobertura</dt>
              <dd>
                {projected
                  ? `${fractionLabel(projected.percentage)} · Sem distribuição ${reais(coverage?.undistributedCents ?? 0)}`
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>Regras e ajustes</dt>
              <dd>
                {[
                  count(preview.wouldDeactivate?.nodeRules, "regra de EAP", "regras de EAP"),
                  count(preview.kept?.resourceRules, "regra de insumo, família ou grupo", "regras de insumo, família ou grupo"),
                  count(preview.wouldDeactivate?.adjustments, "ajuste de mês", "ajustes de mês"),
                ].join(" · ")}
              </dd>
            </div>
          </dl>
          {presentation ? (
            <p className="building-disbursement__note">
              Obra de apresentação: o cronograma volta ao roteiro pelo script de restauração, não por aqui.
            </p>
          ) : (
            <>
              <p className="building-disbursement__source" data-testid="reset-source">
                {`O reset puxa do ${source} · ${resolvedByText(preview.resolvedBy)}`}
              </p>
              {!preview.eligible ? (
                <p className="alert alert-info" role="status" data-testid="reset-ineligible">
                  {`O ${source} não está disponível para esta obra: ${DISCARD_REASON_LABELS[preview.reason] ?? "fonte indisponível nesta obra"}. O reset não usa outra fonte.`}
                </p>
              ) : null}
              {confirming ? (
                <div className="building-disbursement__confirm" role="group" aria-label="Confirmar o reset do cronograma">
                  <p>
                    {`Resetar o cronograma puxando de novo do ${source}? ${count(preview.wouldDeactivate?.nodeRules, "regra de EAP será desativada", "regras de EAP serão desativadas")} e ${count(preview.wouldDeactivate?.adjustments, "ajuste de mês", "ajustes de mês")} também. As regras de insumo, família e grupo e os grupos são mantidos. Tudo fica no histórico.`}
                  </p>
                  <div className="building-disbursement__actions">
                    <button type="button" className="button button-red" disabled={resetting} onClick={confirmReset}>
                      {resetting ? "Resetando…" : "Confirmar reset"}
                    </button>
                    <button type="button" className="button" disabled={resetting} onClick={() => setConfirming(false)}>
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="building-disbursement__actions">
                  <button
                    type="button"
                    className="button button-outline"
                    disabled={!preview.eligible}
                    onClick={() => {
                      setResult(null);
                      setConfirming(true);
                    }}
                  >
                    Resetar cronograma
                  </button>
                </div>
              )}
            </>
          )}
        </>
      ) : null}
      {result?.ok ? (
        <p className="alert alert-info" role="status" data-testid="reset-done">
          {`Cronograma resetado${result.origin ? ` a partir do ${ORIGIN_KIND_LABELS[result.origin] ?? result.origin}` : ""}: ${count(result.reset?.rulesDeactivated, "regra de EAP desativada", "regras de EAP desativadas")} e ${count(result.reset?.adjustmentsDeactivated, "ajuste de mês desativado", "ajustes de mês desativados")}. Os demais campos da obra não foram salvos.`}
        </p>
      ) : null}
      {result && !result.ok ? <p className="alert alert-error" role="alert">{result.message}</p> : null}
    </section>
  );
};

export default BuildingDisbursementSchedule;
